/**
 * career-workshop-runner — the recognition-card interview (route `/workshops/:slug`).
 *
 * ONE unified timeline that serves both moments (course-correction 2026-07-03):
 *  - LIVE during a run: the timeline grows node by node; the current step is the
 *    newest, active node (the interviewer's Überlegung → the card → answer affordances).
 *  - REVISIT after completion: the SAME timeline, complete and scannable, each node
 *    showing what it produced (claim refs into the ledger).
 * Each node is urn-addressed with is-* state classes (is-accepted / is-in-progress /
 * is-skipped / is-locked). Fetch-driven off the server WorkshopView (durable truth);
 * every action returns the fresh view. Card content (prompt / options / narrative) is
 * domain data, rendered as authored; all chrome flows through t().
 */

import {
  KitElement,
  effect,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, ifDefined, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { BreadcrumbToken, I18nToken, RouterToken, apiGet, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { BreadcrumbBus, Crumb, CrumbChoice } from "../domain"
import { narrativeBlocks } from "../lib/narrative"

/**
 * Split a step title into a parent GROUP + SUBSTEP for grouped DISPLAY (§1). Step titles repeat a
 * shared prefix ("Roter Faden — Bewegung", "Roter Faden — Antrieb"); splitting on the em-dash
 * separator recovers the {group, substep} structure so the path can nest substeps under one parent
 * and de-duplicate the prefix. Titles WITHOUT the separator have no group and render flat — this is
 * display-only grouping; the domain step order is untouched.
 */
const STEP_TITLE_SEP = " — "
export function splitStepTitle(title: string): { group: string | null; substep: string } {
  const i = title.indexOf(STEP_TITLE_SEP)
  if (i === -1) return { group: null, substep: title }
  return { group: title.slice(0, i).trim(), substep: title.slice(i + STEP_TITLE_SEP.length).trim() }
}

/** Workshop display names for the breadcrumb Sub-area crumb (mirror the server method-pack names). */
const WORKSHOP_NAME_KEY: Record<string, CareerKey> = {
  "self-knowledge": "breadcrumb.workshop.self-knowledge",
  "search-profile": "breadcrumb.workshop.search-profile",
  profiler: "breadcrumb.workshop.profiler",
}

// ─── Client view types (mirror the server WorkshopView projection) ───────────

interface CardOption {
  id: string
  label: string
  detail?: string
}
interface ElicitationCard {
  id: string
  primitive: string
  eyebrow: string
  prompt: string
  sublegend?: string
  scenario?: string
  options?: CardOption[]
  optionA?: CardOption
  optionB?: CardOption
  axis?: { left: string; right: string }
  seed?: string
  rationale?: string
  denkweise?: string
}
interface TurnAnswer {
  kind: string
  [k: string]: unknown
}
interface Evidence {
  summary: string
  boundary?: string
}
interface TimelineNode {
  stepId: string
  title: string
  primitive: string
  deepProbe?: boolean
  gate: "locked" | "available" | "in_progress" | "accepted"
  card: ElicitationCard | null
  skipped: boolean
  rationale: string | null
  denkweise: string | null
  turns: { answer: TurnAnswer }[]
  evidence: Evidence[]
  producedClaimRefs: string[]
}
/** A consolidated finding for the "Was zählt" rail / fallback (derived from accepted steps). */
interface Finding {
  title: string
  summary: string | null
  inferred: boolean
}
interface SearchConfig {
  roles: string[]
  queries: string[]
  seniority: string[]
  locations: string[]
  excludeKeywords: string[]
  summary: string
}
interface ProducedContext {
  urn: string
  kind: string
  raw: Record<string, unknown>
  narrative: { body: string; provenance: unknown[]; meta: { degraded?: boolean } } | null
}
interface WorkshopView {
  id: string
  type: string
  status: "in_progress" | "completed"
  cursor: number
  steps: TimelineNode[]
  producedContext: ProducedContext | null
  targeting: SearchConfig | null
}

/**
 * The two poles of a pairwise card, recovering the deployed field-migration on the READ
 * side: the model authored ≈75% of pairwise cards with both poles in `options[]` and no
 * optionA/optionB. Cards already persisted that way (the operator's stuck prod run) must
 * still render — so treat `optionA`/`optionB` when present, else an exactly-2 `options[]`
 * as the two poles. The api-side `repairCardStructure` fixes new cards; this unsticks the
 * old ones with zero data surgery. Anything else → empty → degraded state.
 */
function pairwisePoles(card: ElicitationCard): CardOption[] {
  const both = [card.optionA, card.optionB].filter((o): o is CardOption => Boolean(o))
  if (both.length === 2) return both
  if (card.options?.length === 2) return card.options
  return []
}

/** Only vignette/simulation frame a `scenario`. The deployed model also emitted a stray
 *  scenario string on EVERY pairwise card; gating the "Situation" block to the primitives
 *  that own it keeps that stray field from rendering an out-of-place situation. */
function cardUsesScenario(primitive: string): boolean {
  return primitive === "vignette" || primitive === "simulation"
}

/**
 * A card is DEGRADED when its primitive's renderer has nothing answerable to draw — a
 * `pairwise` with fewer than two recoverable poles, an options primitive with fewer than
 * two options, a `semantic_differential` without an axis. Rather than render a silently
 * empty list (the deployed workshop bug), such a card shows an honest note + the skip
 * affordance. This is the UI's own defense; the api-side probe already repairs/regenerates
 * these (the two layers agree on the rule but neither depends on the other). Free-text
 * primitives (`why_ladder` / `open_narrative`) always have a usable textarea and are never
 * degraded; `card_sort` has its own honest "unsupported" state.
 */
function isCardDegraded(card: ElicitationCard): boolean {
  switch (card.primitive) {
    case "pairwise":
      return pairwisePoles(card).length < 2
    case "best_worst":
    case "vignette":
    case "simulation":
      return (card.options?.length ?? 0) < 2
    case "semantic_differential":
      return !card.axis
    default:
      return false
  }
}

const TARGETING_FIELDS: { key: keyof SearchConfig; label: CareerKey }[] = [
  { key: "roles", label: "workshop.targeting.roles" },
  { key: "queries", label: "workshop.targeting.queries" },
  { key: "seniority", label: "workshop.targeting.seniority" },
  { key: "locations", label: "workshop.targeting.locations" },
  { key: "excludeKeywords", label: "workshop.targeting.exclusions" },
]

export class WorkshopRunnerVm implements ViewModel {
  readonly workshop: Signal<WorkshopView | null> = signal<WorkshopView | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** True when the slug names no workshop (API 404) — a clean not-found, not a raw error. */
  readonly notFound: Signal<boolean> = signal(false)
  readonly loaded: Signal<boolean> = signal(false)
  /** "generate" | "submit" | "consolidate" | "targeting" | null */
  readonly busy: Signal<string | null> = signal<string | null>(null)
  // Local answer selections for multi-part cards.
  readonly bestId: Signal<string | null> = signal<string | null>(null)
  readonly worstId: Signal<string | null> = signal<string | null>(null)
  readonly choiceId: Signal<string | null> = signal<string | null>(null)
  readonly vetoes: Signal<string[]> = signal<string[]>([])
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  readonly slug: string

  constructor(private readonly router: Router, i18n: CareerI18n, private readonly breadcrumb: BreadcrumbBus, urn: Urn | null) {
    this.t = i18n.t
    this.slug = (urn ?? "").split(":").pop() ?? ""
    this.renderDeps = [this.workshop, this.error, this.notFound, this.loaded, this.busy, this.bestId, this.worstId, this.choiceId, this.vetoes]
  }

  attach(): () => void {
    void this.refresh().then(() => this.maybeAutoGenerate())
    // Contribute the deeper breadcrumb segments (§7a): Sub-area = workshop name, Step =
    // current step + a dropdown of sibling steps. Recomputes as the workshop loads/advances.
    const stopCrumbs = effect([this.workshop, this.notFound], () => this.breadcrumb.set(this.buildBreadcrumb()))
    return () => {
      stopCrumbs()
      this.breadcrumb.clear()
    }
  }

  /** Sub-area (workshop name) + Step (current step, with a sibling-step dropdown) crumbs (§7a). */
  private buildBreadcrumb(): Crumb[] {
    const nameKey = WORKSHOP_NAME_KEY[this.slug]
    const subArea: Crumb = { label: nameKey ? this.t(nameKey) : this.t("surface.workshopRunner.title") }
    const w = this.workshop.value
    if (this.notFound.value || !w || w.steps.length === 0) return [subArea]
    const cursor = w.status === "completed" ? w.steps.length - 1 : w.cursor
    // Present the SAME parent→substep grouping as the "Dein Weg" rail (§1/§7a): a non-interactive
    // group heading once per parent, then its substeps (de-duplicated label); steps without the
    // separator render flat. Headings carry neither url nor stepId, so they read as labels only.
    const choices: CrumbChoice[] = []
    let prevGroup: string | null = null
    for (const [i, s] of w.steps.entries()) {
      const { group, substep } = splitStepTitle(s.title)
      if (group && group !== prevGroup) choices.push({ label: group, current: false, heading: true })
      prevGroup = group
      choices.push({ label: group ? substep : s.title, current: i === cursor, stepId: s.stepId })
    }
    // The collapsed step label keeps the FULL title so the group prefix stays visible in the bar;
    // the parent→substep grouping is expressed inside the dropdown (choices above).
    const step: Crumb = { label: w.steps[cursor]?.title ?? subArea.label, content: true, choices }
    return [subArea, step]
  }

  private async refresh(): Promise<void> {
    try {
      this.workshop.set(await apiGet<WorkshopView | null>(`/api/workshops/${this.slug}`))
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      // An unknown slug is a clean not-found (API 404), not a surfaced technical error. A known
      // slug that is simply not started returns 200 with a null body (the not-started state).
      if (/HTTP 404|no such workshop/i.test(msg)) this.notFound.set(true)
      else this.error.set(msg)
    } finally {
      this.loaded.set(true)
    }
  }

  /** The current (cursor) step — the active node. */
  currentStep(): TimelineNode | null {
    const w = this.workshop.value
    if (!w || w.status === "completed") return null
    return w.steps[w.cursor] ?? null
  }

  /** Auto-generate the current step's stimulus when it is available and card-less. */
  private maybeAutoGenerate(): void {
    const step = this.currentStep()
    if (step && !step.card && !step.skipped && this.busy.value === null) void this.generate()
  }

  private clearSelections(): void {
    this.bestId.set(null)
    this.worstId.set(null)
    this.choiceId.set(null)
    this.vetoes.set([])
  }

  async start(): Promise<void> {
    this.busy.set("generate")
    try {
      await apiPost(`/api/workshops/${this.slug}/start`)
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(null)
      this.maybeAutoGenerate()
    }
  }

  async generate(): Promise<void> {
    const step = this.currentStep()
    if (!step) return
    this.busy.set("generate")
    this.error.set(null)
    try {
      const res = await apiPost<{ workshop: WorkshopView | null }>(`/api/workshops/${this.slug}/stimulus`, { slug: this.slug, stepId: step.stepId })
      this.workshop.set(res.workshop)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(null)
      // A skipped step advances; pull the next stimulus.
      if (this.currentStep() && !this.currentStep()!.card) this.maybeAutoGenerate()
    }
  }

  async submit(answer: TurnAnswer): Promise<void> {
    const step = this.currentStep()
    if (!step) return
    this.busy.set("submit")
    this.error.set(null)
    try {
      const res = await apiPost<{ workshop: WorkshopView | null }>(`/api/workshops/${this.slug}/turn`, {
        slug: this.slug,
        stepId: step.stepId,
        answer: { ...answer, cardId: step.card?.id ?? step.stepId },
      })
      this.workshop.set(res.workshop)
      this.clearSelections()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(null)
      this.maybeAutoGenerate()
    }
  }

  async consolidate(): Promise<void> {
    this.busy.set("consolidate")
    this.error.set(null)
    try {
      const res = await apiPost<{ workshop: WorkshopView | null }>(`/api/workshops/${this.slug}/consolidate`, { slug: this.slug })
      this.workshop.set(res.workshop)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(null)
    }
  }

  async saveTargeting(config: SearchConfig): Promise<void> {
    this.busy.set("targeting")
    try {
      const res = await apiPost<{ workshop: WorkshopView | null }>(`/api/workshops/search-profile/targeting`, { config })
      this.workshop.set(res.workshop)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(null)
    }
  }

  toggleVeto(id: string): void {
    const cur = this.vetoes.value
    this.vetoes.set(cur.includes(id) ? cur.filter((v) => v !== id) : [...cur, id])
  }

  allAnswered(): boolean {
    const w = this.workshop.value
    return Boolean(w && w.steps.every((s) => s.gate === "accepted"))
  }

  requiredAnswered(): boolean {
    const w = this.workshop.value
    return Boolean(w && w.steps.filter((s) => !s.deepProbe).every((s) => s.gate === "accepted"))
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerWorkshopRunner extends KitElement<WorkshopRunnerVm> {
  protected createViewModel(ctx: KitContext): WorkshopRunnerVm {
    return new WorkshopRunnerVm(ctx.inject(RouterToken), ctx.inject(I18nToken), ctx.inject(BreadcrumbToken), this.urn ?? null)
  }

  // ── Card rendering per primitive ──────────────────────────────────────────

  /** The legend row whose column labels sit pixel-aligned over the reaction cells. */
  private reactionLegend(vm: WorkshopRunnerVm, cols: { cls: string; key: CareerKey }[]): TemplateResult {
    return html`<div class="legend" aria-hidden="true">
      <span></span>
      <div class=${classMap({ "legend-cols": true, [`legend-cols-${cols.length}`]: true })}>
        ${cols.map((c) => html`<span class=${c.cls} lang="de">${vm.t(c.key)}</span>`)}
      </div>
    </div>`
  }

  /** The confirm button under a recognition card (shared shape; disabled until answerable). */
  private submitButton(vm: WorkshopRunnerVm, kind: string, disabled: boolean): TemplateResult {
    const submitting = vm.busy.value === "submit"
    return html`<div class="card-actions">
      <button class="primary" data-act="submit-turn" data-kind=${kind} ?disabled=${disabled} lang="de">${submitting ? vm.t("workshop.card.submitting") : vm.t("workshop.card.submit")}</button>
    </div>`
  }

  /** Honest fallback for a card whose primitive data is missing/insufficient: a quiet
   *  note (no mechanism talk) + the existing skip affordance so the user can move on. */
  private degradedCard(vm: WorkshopRunnerVm): TemplateResult {
    const submitting = vm.busy.value === "submit"
    return html`
      <div class="card-degraded" role="status">
        <p class="surface-empty" lang="de">${vm.t("workshop.card.degraded")}</p>
        <div class="card-actions">
          <button class="ghost card-degraded-skip" data-act="mark-nuance" data-nuance="depends" ?disabled=${submitting} lang="de">${vm.t("workshop.card.degradedSkip")}</button>
        </div>
      </div>`
  }

  // ── Card body per primitive — flat recognition rows in ONE visual grammar (§3) ──
  private cardBody(vm: WorkshopRunnerVm, step: TimelineNode, card: ElicitationCard): TemplateResult {
    const submitting = vm.busy.value === "submit"
    const consolidating = vm.busy.value === "consolidate"
    const optionalReady = step.deepProbe && vm.requiredAnswered()
    if (isCardDegraded(card)) return this.degradedCard(vm)
    switch (card.primitive) {
      case "pairwise": {
        const opts = pairwisePoles(card)
        return html`
          ${this.reactionLegend(vm, [{ cls: "c-choose", key: "workshop.card.choose" }])}
          <ul class="options" role="group">
            ${opts.map(
              (o, i) => html`<li class="opt">
                <div class="opt-main"><p class="opt-label" lang="de">${o.label}</p>${o.detail ? html`<p class="opt-detail" lang="de">${o.detail}</p>` : nothing}</div>
                <div class="opt-cells opt-cells-1">
                  <button class="cell k-choose" data-act="submit-turn" data-kind="pairwise" data-choice=${i === 0 ? "A" : "B"} aria-label=${o.label} ?disabled=${submitting} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✓</span></button>
                </div>
              </li>`,
            )}
          </ul>`
      }
      case "best_worst": {
        const opts = card.options ?? []
        return html`
          ${this.reactionLegend(vm, [{ cls: "c-choose", key: "workshop.card.best" }, { cls: "c-nogo", key: "workshop.card.worst" }])}
          <ul class="options" role="group">
            ${opts.map((o) => {
              const most = vm.bestId.value === o.id
              const least = vm.worstId.value === o.id
              return html`<li class=${classMap({ opt: true, "is-most": most, "is-least": least })}>
                <div class="opt-main"><p class="opt-label" lang="de">${o.label}</p>${o.detail ? html`<p class="opt-detail" lang="de">${o.detail}</p>` : nothing}</div>
                <div class="opt-cells opt-cells-2">
                  <button class="cell k-most" data-act="pick-option" data-role="best" data-id=${o.id} aria-pressed=${most ? "true" : "false"} aria-label=${vm.t("workshop.card.best")} ?disabled=${submitting} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✓</span></button>
                  <button class="cell k-least" data-act="pick-option" data-role="worst" data-id=${o.id} aria-pressed=${least ? "true" : "false"} aria-label=${vm.t("workshop.card.worst")} ?disabled=${submitting} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">▽</span></button>
                </div>
              </li>`
            })}
          </ul>
          ${this.submitButton(vm, "best_worst", submitting || !vm.bestId.value || !vm.worstId.value)}`
      }
      case "vignette":
      case "simulation": {
        const opts = card.options ?? []
        return html`
          ${this.reactionLegend(vm, [{ cls: "c-choose", key: "workshop.card.choose" }, { cls: "c-nogo", key: "workshop.card.veto" }])}
          <ul class="options" role="group">
            ${opts.map((o) => {
              const chosen = vm.choiceId.value === o.id
              const vetoed = vm.vetoes.value.includes(o.id)
              return html`<li class=${classMap({ opt: true, "is-chosen": chosen, "is-nogo": vetoed })}>
                <div class="opt-main"><p class="opt-label" lang="de">${o.label}</p>${o.detail ? html`<p class="opt-detail" lang="de">${o.detail}</p>` : nothing}</div>
                <div class="opt-cells opt-cells-2">
                  <button class="cell k-choose" data-act="pick-option" data-role="choice" data-id=${o.id} aria-pressed=${chosen ? "true" : "false"} aria-label=${vm.t("workshop.card.choose")} ?disabled=${submitting} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✓</span></button>
                  <button class="cell k-nogo" data-act="toggle-veto" data-id=${o.id} aria-pressed=${vetoed ? "true" : "false"} aria-label=${vm.t("workshop.card.veto")} ?disabled=${submitting} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✕</span></button>
                </div>
              </li>`
            })}
          </ul>
          ${this.submitButton(vm, card.primitive, submitting || !vm.choiceId.value)}`
      }
      case "semantic_differential":
        return html`
          <div class="card-differential">
            <span class="axis-pole" lang="de">${card.axis?.left ?? ""}</span>
            <input type="range" min="0" max="1" step="0.01" value="0.5" aria-label=${card.prompt} class="differential-input" />
            <span class="axis-pole" lang="de">${card.axis?.right ?? ""}</span>
          </div>
          ${this.submitButton(vm, "semantic_differential", submitting)}`
      case "why_ladder":
      case "open_narrative":
        // §5 flat input — the real free-text seam of the contract; no box, an underline
        // that strengthens on hover and animates L→R on focus, auto-growing via CSS.
        return html`
          <label class="flat-field">
            <textarea class="card-narrative" rows="1" placeholder=${vm.t("workshop.card.narrativePlaceholder")} aria-label=${card.prompt} lang="de"></textarea>
            <span class="rule" aria-hidden="true"></span>
          </label>
          <div class="card-actions">
            <button class="primary" data-act="submit-turn" data-kind="open_narrative" ?disabled=${submitting || consolidating} lang="de">${submitting ? vm.t("workshop.card.submitting") : vm.t("workshop.card.submit")}</button>
            ${optionalReady
              ? html`<button class="card-skip-optional" data-act="consolidate-workshop" ?disabled=${consolidating} lang="de">
                  ${consolidating ? vm.t("workshop.consolidate.busy") : vm.t("workshop.card.skipOptional")}
                </button>`
              : nothing}
          </div>`
      default:
        // card_sort + any kind the packs never generate: honest unsupported state (no crash).
        return html`<p class="card-unsupported" role="status" lang="de">${vm.t("workshop.card.unsupported")}</p>`
    }
  }

  /** The full-detail active card — card-type tag, situation, question, reaction rows, nuance. */
  private activeCard(vm: WorkshopRunnerVm, step: TimelineNode): TemplateResult {
    const card = step.card
    if (!card) {
      const generating = vm.busy.value === "generate"
      return html`<div class="card card-generate">
        <button class=${classMap({ primary: true, "is-pending": generating })} data-act="generate-stimulus" ?disabled=${generating} lang="de">${generating ? vm.t("workshop.card.generating") : vm.t("workshop.card.generate")}</button>
      </div>`
    }
    const submitting = vm.busy.value === "submit"
    const isNarrative = card.primitive === "why_ladder" || card.primitive === "open_narrative"
    return html`
      <div class="card">
        <span class="card-type" lang="de">${card.eyebrow}</span>
        ${card.scenario && cardUsesScenario(card.primitive)
          ? html`<div class="card-scenario situation">
              <span class="eyebrow situation-eyebrow" lang="de">${vm.t("workshop.card.situation")}</span>
              <p lang="de">${card.scenario}</p>
            </div>`
          : nothing}
        <p class="card-prompt q" lang="de">${card.prompt}</p>
        ${card.sublegend ? html`<p class="card-sublegend sublegend" lang="de">${card.sublegend}</p>` : nothing}
        ${step.deepProbe ? html`<p class="card-optional-cue chip" lang="de">${vm.t("workshop.node.optional")}</p>` : nothing}
        ${this.cardBody(vm, step, card)}
        ${isNarrative || isCardDegraded(card)
          ? nothing
          : html`<div class="unsure"><span class="unsure-label" lang="de">${vm.t("workshop.card.unsureLabel")}</span><button class="ghost card-notsure" data-act="mark-nuance" data-nuance="depends" ?disabled=${submitting} lang="de">${vm.t("workshop.card.notSure")}</button></div>`}
      </div>`
  }

  /** A short human read of a past node's answer — the folded German evidence summary. */
  private answerSummary(step: TimelineNode): string | null {
    if (step.evidence[0]?.summary) return step.evidence[0].summary
    const a = step.turns[0]?.answer
    if (a?.kind === "open_narrative" && typeof a.text === "string") return a.text
    return null
  }

  // ── Two voices (§4) — from the fields the domain already carries. ──────────────
  /** Default-visible Rationale (guidance): a short, personal read above the turn. */
  private rationaleOf(step: TimelineNode): string | null {
    return step.rationale ?? step.card?.rationale ?? null
  }
  /** Disclosed Überlegung (the distinct second voice — "why this card was asked"). Rendered
   *  only when the interviewer authored one AND it differs from the visible rationale. */
  private denkweiseOf(step: TimelineNode): string | null {
    const d = step.denkweise ?? step.card?.denkweise ?? null
    return d && d !== this.rationaleOf(step) ? d : null
  }
  private rationaleBlock(text: string | null): TemplateResult {
    return text ? html`<p class="node-rationale guidance" lang="de">${text}</p>` : (nothing as unknown as TemplateResult)
  }
  private denkweiseBlock(vm: WorkshopRunnerVm, text: string | null): TemplateResult {
    return text
      ? html`<details class="node-ueberlegung denkweise">
          <summary lang="de">${vm.t("workshop.node.ueberlegung")}</summary>
          <p class="denkweise-body" lang="de">${text}</p>
        </details>`
      : (nothing as unknown as TemplateResult)
  }

  /** Expanding a past turn shows the WHOLE original question, read-only — never re-summarized. */
  private recap(vm: WorkshopRunnerVm, step: TimelineNode): TemplateResult {
    const card = step.card
    if (!card) return nothing as unknown as TemplateResult
    return html`<div class="recap">
      ${card.scenario && cardUsesScenario(card.primitive)
        ? html`<div class="card-scenario situation">
            <span class="eyebrow situation-eyebrow" lang="de">${vm.t("workshop.card.situation")}</span>
            <p lang="de">${card.scenario}</p>
          </div>`
        : nothing}
      <p class="recap-q" lang="de">${card.prompt}</p>
      <ul class="recap-opts">${this.recapOpts(step, card)}</ul>
    </div>`
  }

  /** The original options with the user's choice highlighted (read-only, per primitive). */
  private recapOpts(step: TimelineNode, card: ElicitationCard): TemplateResult[] {
    const answer = step.turns[0]?.answer
    const pick = (v: unknown): string | null => (typeof v === "string" ? v : null)
    const li = (name: string, poleCls: "is-chosen" | "is-least" | "", glyph: string): TemplateResult =>
      html`<li class=${classMap({ "recap-opt": true, ...(poleCls ? { [poleCls]: true } : {}) })}>
        <span class="recap-mark" aria-hidden="true">${glyph}</span><span class="recap-name" lang="de">${name}</span>
      </li>`
    if (card.primitive === "pairwise") {
      const opts = pairwisePoles(card)
      const choice = pick(answer?.["choice"])
      return opts.map((o, i) => {
        const chosen = (i === 0 ? "A" : "B") === choice
        return li(o.label, chosen ? "is-chosen" : "", chosen ? "✓" : "·")
      })
    }
    if (card.primitive === "best_worst") {
      const best = pick(answer?.["best"])
      const worst = pick(answer?.["worst"])
      return (card.options ?? []).map((o) =>
        o.id === best ? li(o.label, "is-chosen", "✓") : o.id === worst ? li(o.label, "is-least", "▽") : li(o.label, "", "·"),
      )
    }
    if (card.primitive === "vignette" || card.primitive === "simulation") {
      const choice = pick(answer?.["choice"])
      const vetoes = answer && Array.isArray(answer["vetoes"]) ? (answer["vetoes"] as { id?: string }[]).map((v) => v.id) : []
      return (card.options ?? []).map((o) =>
        o.id === choice ? li(o.label, "is-chosen", "✓") : vetoes.includes(o.id) ? li(o.label, "is-least", "✕") : li(o.label, "", "·"),
      )
    }
    const text = pick(answer?.["text"])
    return text ? [li(text, "is-chosen", "✓")] : []
  }

  private producedLink(vm: WorkshopRunnerVm, step: TimelineNode): TemplateResult {
    return step.producedClaimRefs.length > 0
      ? html`<a class="node-produced chip" href="/compass/evidence" data-act="nav" data-url="/compass/evidence" lang="de">${vm.t("workshop.node.produced", { n: step.producedClaimRefs.length })}</a>`
      : (nothing as unknown as TemplateResult)
  }

  /** One timeline element, rendered at fisheye detail by distance from the active turn (§2). */
  private timelineNode(vm: WorkshopRunnerVm, step: TimelineNode, index: number, cursor: number, completed: boolean, total: number): TemplateResult {
    const isActive = !completed && index === cursor
    const optionalLeftOpen = completed && step.deepProbe && step.gate !== "accepted"
    const skipped = step.skipped || optionalLeftOpen
    const accepted = step.gate === "accepted"
    const gate = accepted ? (step.skipped ? "is-skipped" : "is-accepted") : optionalLeftOpen ? "is-skipped" : isActive ? "is-in-progress" : "is-locked"
    const recent = accepted && ((!completed && index === cursor - 1) || (completed && index === total - 1))
    const distance = isActive ? "turn-active" : accepted ? (recent ? "turn-recent" : "turn-past") : skipped ? "turn-past" : "turn-upcoming"
    const urn = `urn:career:workshop-step:${step.stepId}` as Urn
    const rationale = this.rationaleOf(step)
    const denkweise = this.denkweiseOf(step)
    const summary = this.answerSummary(step)
    const cls = { "timeline-node": true, turn: true, [gate]: true, [distance]: true }

    if (isActive) {
      return html`<li urn=${urn} class=${classMap(cls)}>
        ${this.rationaleBlock(rationale)}
        ${this.denkweiseBlock(vm, denkweise)}
        <div class="node-active">${this.activeCard(vm, step)}</div>
      </li>`
    }
    if (skipped) {
      return html`<li urn=${urn} class=${classMap(cls)}>
        <details class="turn-collapsed">
          <summary class="turn-line">
            <span class="turn-q" lang="de">${step.title}</span>
            <span class="turn-ans node-skipped is-skipped" lang="de">${vm.t("workshop.node.skipped")}</span>
            <span class="turn-caret" aria-hidden="true">›</span>
          </summary>
          <div class="turn-peek">${this.rationaleBlock(rationale)}</div>
        </details>
      </li>`
    }
    if (accepted) {
      return html`<li urn=${urn} class=${classMap(cls)}>
        ${recent ? this.rationaleBlock(rationale) : nothing}
        ${recent ? this.denkweiseBlock(vm, denkweise) : nothing}
        <details class="turn-collapsed">
          <summary class="turn-line">
            <span class="turn-q" lang="de">${step.card?.prompt ?? step.title}</span>
            ${summary ? html`<span class="turn-ans node-answer" lang="de">${summary}</span>` : nothing}
            <span class="turn-caret" aria-hidden="true">›</span>
          </summary>
          <div class="turn-peek">
            ${recent ? nothing : this.rationaleBlock(rationale)}
            ${recent ? nothing : this.denkweiseBlock(vm, denkweise)}
            ${this.recap(vm, step)}
            ${this.producedLink(vm, step)}
          </div>
        </details>
      </li>`
    }
    // upcoming / locked — a browseable placeholder, not a card.
    return html`<li urn=${urn} class=${classMap(cls)}>
      <div class="turn-upcoming-line">
        <span class="turn-q" lang="de">${step.title}</span>
        <span class="node-pending is-locked" lang="de">${vm.t("workshop.node.pending")}</span>
      </div>
    </li>`
  }

  // ── Consolidation view ────────────────────────────────────────────────────

  private rawContext(vm: WorkshopRunnerVm, ctx: ProducedContext, targeting: SearchConfig | null): TemplateResult {
    const raw = ctx.raw
    const themeList = (title: CareerKey, items: { label?: string; detail?: string }[]) =>
      items.length > 0
        ? html`<div class="context-group">
            <h4 lang="de">${vm.t(title)}</h4>
            <ul role="list">${items.map((t) => html`<li lang="de">${t.detail || t.label}</li>`)}</ul>
          </div>`
        : nothing
    if (ctx.kind === "candidate") {
      return html`
        ${themeList("workshop.context.themes", (raw.themes as { label?: string; detail?: string }[]) ?? [])}
        ${themeList("workshop.context.strengths", (raw.strengths as { label?: string; detail?: string }[]) ?? [])}
        ${themeList("workshop.context.gaps", (raw.gaps as { label?: string; detail?: string }[]) ?? [])}`
    }
    if (ctx.kind === "search") {
      // Render the EFFECTIVE targeting (the single source of truth the editor also shows and
      // slice-4 search consumes), NOT the consolidation-time `raw.config` snapshot — that snapshot
      // goes stale after a targeting save and would contradict the editor for the same field.
      const cfg = targeting ?? (raw.config as SearchConfig) ?? { roles: [], queries: [], seniority: [], locations: [], excludeKeywords: [], summary: "" }
      return html`<dl class="context-config">
        ${TARGETING_FIELDS.map(
          (f) => html`<dt lang="de">${vm.t(f.label)}</dt><dd lang="de">${(cfg[f.key] as string[]).length > 0 ? (cfg[f.key] as string[]).join(", ") : "—"}</dd>`,
        )}
      </dl>`
    }
    // profiler_rubric
    const criteria = (raw.criteria as { label: string; description?: string; weight: number }[]) ?? []
    const anti = (raw.antiValues as string[]) ?? []
    return html`
      <div class="context-group">
        <h4 lang="de">${vm.t("workshop.context.criteria")}</h4>
        <ul role="list">${criteria.map((c) => html`<li lang="de">${c.label}${c.description ? ` — ${c.description}` : ""}</li>`)}</ul>
      </div>
      ${anti.length > 0 ? html`<div class="context-group"><h4 lang="de">${vm.t("workshop.context.antiValues")}</h4><ul role="list">${anti.map((a) => html`<li lang="de">${a}</li>`)}</ul></div>` : nothing}`
  }

  private targetingEditor(vm: WorkshopRunnerVm, cfg: SearchConfig): TemplateResult {
    const saving = vm.busy.value === "targeting"
    return html`
      <section class="workshop-targeting surface-panel" aria-labelledby="targeting-title">
        <h3 id="targeting-title" class="surface-section-title" lang="de">${vm.t("workshop.targeting.title")}</h3>
        <p class="surface-hint" lang="de">${vm.t("workshop.targeting.hint")}</p>
        <form class="targeting-form">
          ${TARGETING_FIELDS.map(
            (f) => html`<label class="targeting-field">
              <span lang="de">${vm.t(f.label)}</span>
              <textarea name=${f.key} rows="2" lang="de">${(cfg[f.key] as string[]).join("\n")}</textarea>
            </label>`,
          )}
          <button data-act="update-targeting" class=${classMap({ "is-pending": saving })} ?disabled=${saving} lang="de">${saving ? vm.t("workshop.card.submitting") : vm.t("workshop.targeting.save")}</button>
        </form>
      </section>`
  }

  private consolidationView(vm: WorkshopRunnerVm, w: WorkshopView): TemplateResult {
    const ctx = w.producedContext
    if (!ctx) return nothing as unknown as TemplateResult
    const n = ctx.narrative
    return html`
      <section class="workshop-context surface-panel" aria-labelledby="context-title">
        <h2 id="context-title" class="surface-section-title" lang="de">${vm.t("workshop.context.title")}</h2>
        ${this.rawContext(vm, ctx, w.targeting)}
        ${n
          ? html`<div class=${classMap({ "context-narrative": true, "is-degraded": Boolean(n.meta.degraded) })} aria-live="polite">
              ${n.meta.degraded ? html`<p class="narrative-degraded-cue" role="status" lang="de">${vm.t("workshop.narrative.degraded")}</p>` : nothing}
              <div class="narrative-body">${narrativeBlocks(n.body).map((b) => (b.heading ? html`<h3 lang="de">${b.text}</h3>` : html`<p lang="de">${b.text}</p>`))}</div>
              <p class="narrative-meta"><span class="narrative-prov chip" lang="de">${vm.t("review.narrative.provenance", { n: n.provenance.length })}</span></p>
            </div>`
          : nothing}
      </section>
      ${w.type === "search_profile" && w.targeting ? this.targetingEditor(vm, w.targeting) : nothing}`
  }

  // ── §1 LEFT path — the workshop's steps as a numbered, stateful progress rail. ──
  private pathRail(vm: WorkshopRunnerVm, w: WorkshopView, completed: boolean): TemplateResult {
    // Progress-fill spine: fraction of accepted steps drives --ws-progress (0..1), consumed by a
    // hard two-stop gradient on .ws-path-steps::before. The axis (left/width) is untouched.
    const total = w.steps.length
    const progress = total > 0 ? w.steps.filter((s) => s.gate === "accepted").length / total : 0
    // Group steps under their shared parent (§1): a group heading once per parent, then its substeps
    // (de-duplicated label, indented); steps without the " — " separator render flat. Numbering stays
    // global (i+1) and the progress spine is untouched — grouping is a display concern only.
    let prevGroup: string | null = null
    return html`<nav class="ws-path" aria-label=${vm.t("workshop.timeline.title")}>
      <p class="ws-path-title eyebrow" lang="de">${vm.t("workshop.timeline.title")}</p>
      <ol class="ws-path-steps" style=${`--ws-progress:${progress}`}>
        ${w.steps.map((s, i) => {
          const active = !completed && i === w.cursor
          const done = s.gate === "accepted"
          // Reachable steps (accepted, or the current one) jump the center log to their turn; the
          // rest are inert (not focusable, no pointer) — a control only where navigation is real.
          const reachable = done || active
          const state = done ? "is-accepted" : active ? "is-in-progress" : "is-locked"
          const { group, substep } = splitStepTitle(s.title)
          const label = group ? substep : s.title
          const newGroup = group && group !== prevGroup ? group : null
          prevGroup = group
          const inner = html`<span class="ws-num" aria-hidden="true">${done ? "✓" : String(i + 1)}</span><span class="ws-step-label" lang="de">${label}</span>`
          return html`${newGroup ? html`<li class="ws-group" aria-hidden="true" lang="de">${newGroup}</li>` : nothing}<li
            class=${classMap({ "ws-step": true, [state]: true, "is-current": active, "is-substep": Boolean(group) })}
            aria-current=${ifDefined(active ? "step" : undefined)}
          >
            ${reachable
              ? html`<button class="ws-step-row" type="button" data-act="goto-step" data-step-id=${s.stepId}>${inner}</button>`
              : html`<div class="ws-step-row">${inner}</div>`}
          </li>`
        })}
      </ol>
    </nav>`
  }

  // ── §1 findings ("Was zählt"): confirmed/inferred findings + open threads. Projected once,
  //    rendered two ways — the sticky aside (>1040px) and the collapsible <details> fallback
  //    (≤1040px). Both share findingsBody so the content is identical. ──
  private projectFindings(w: WorkshopView): {
    findings: Finding[]
    threads: WorkshopView["steps"]
  } {
    const findings = w.steps
      .filter((s) => s.gate === "accepted")
      .flatMap((s) => {
        const summary = s.evidence[0]?.summary ?? null
        return summary || s.skipped ? [{ title: s.title, summary, inferred: s.skipped }] : []
      })
    const threads = w.steps.filter((s) => s.gate !== "accepted")
    return { findings, threads }
  }

  private findingsBody(vm: WorkshopRunnerVm, findings: Finding[], threads: WorkshopView["steps"]): TemplateResult {
    return html`
      <p class="ws-findings-lede" lang="de">${vm.t("workshop.findings.lede")}</p>
      ${findings.length > 0
        ? html`<ul class="finds">
            ${findings.map(
              (f, i) => html`<li class=${classMap({ find: true, "is-confirmed": !f.inferred, "is-inferred": f.inferred, "is-latest": i === findings.length - 1 })}>
                <div class="find-top"><span class="find-mark" aria-hidden="true">${f.inferred ? "?" : "✓"}</span><span class="find-name" lang="de">${f.title}</span></div>
                ${f.summary ? html`<p class="find-desc" lang="de">${f.summary}</p>` : nothing}
                <span class="find-tag" lang="de">${vm.t(f.inferred ? "workshop.findings.inferred" : "workshop.findings.confirmed")}</span>
              </li>`,
            )}
          </ul>`
        : html`<p class="ws-findings-empty" lang="de">${vm.t("workshop.findings.empty")}</p>`}
      ${threads.length > 0
        ? html`<p class="threads-title eyebrow" lang="de">${vm.t("workshop.findings.threads")}</p>
            <ul class="threads">${threads.map((s) => html`<li lang="de">${s.title}</li>`)}</ul>`
        : nothing}`
  }

  // §1 RIGHT — sticky rail (>1040px). Hidden below via CSS; the fallback below serves there.
  private findingsRail(vm: WorkshopRunnerVm, w: WorkshopView): TemplateResult {
    const { findings, threads } = this.projectFindings(w)
    return html`<aside class="ws-findings" aria-label=${vm.t("workshop.findings.title")}>
      <div class="ws-findings-head">
        <span class="eyebrow" lang="de">${vm.t("workshop.findings.title")}</span>
        <span class="ws-findings-count num">${findings.length}</span>
      </div>
      ${this.findingsBody(vm, findings, threads)}
    </aside>`
  }

  // §1 findings fallback — a native <details> in the reading flow, shown only ≤1040px (CSS).
  // Collapsed by default; the summary carries the same count pill. Sits at the END of the log
  // so it never interleaves with the running interview.
  private findingsFallback(vm: WorkshopRunnerVm, w: WorkshopView): TemplateResult {
    const { findings, threads } = this.projectFindings(w)
    return html`<details class="ws-findings-fallback">
      <summary>
        <span class="eyebrow" lang="de">${vm.t("workshop.findings.title")}</span>
        <span class="ws-findings-count num">${findings.length}</span>
        <svg class="ws-findings-fallback-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </summary>
      <div class="ws-findings-fallback-body">${this.findingsBody(vm, findings, threads)}</div>
    </details>`
  }

  // ── §1 LEFT back-nav fallback — a compact horizontal step strip, shown only ≤760px (CSS).
  //    Numbered .ws-num dots on a connector spine; reachable steps are <button data-act="goto-step">
  //    — the SAME jump the full .ws-path rail triggers (no forked navigation). A caption names the
  //    current step so "where am I" is legible without the full rail. ──
  private pathStrip(vm: WorkshopRunnerVm, w: WorkshopView, completed: boolean): TemplateResult {
    const total = w.steps.length
    const progress = total > 0 ? w.steps.filter((s) => s.gate === "accepted").length / total : 0
    const pos = Math.min(Math.max(w.cursor, 0), Math.max(total - 1, 0))
    const cur = w.steps[pos]
    const curLabel = cur ? splitStepTitle(cur.title).substep : ""
    return html`<nav class="ws-path-strip" aria-label=${vm.t("workshop.timeline.title")} style=${`--ws-progress:${progress}`}>
      <ol class="ws-strip-scroll">
        ${w.steps.map((s, i) => {
          const active = !completed && i === w.cursor
          const done = s.gate === "accepted"
          const reachable = done || active
          const state = done ? "is-accepted" : active ? "is-in-progress" : "is-locked"
          const { group, substep } = splitStepTitle(s.title)
          const label = group ? substep : s.title
          const inner = html`<span class="ws-num" aria-hidden="true">${done ? "✓" : String(i + 1)}</span>`
          return html`<li
            class=${classMap({ "ws-strip-step": true, [state]: true, "is-current": active })}
            aria-current=${ifDefined(active ? "step" : undefined)}
          >
            ${reachable
              ? html`<button class="ws-strip-btn" type="button" data-act="goto-step" data-step-id=${s.stepId} title=${label} aria-label=${label}>${inner}</button>`
              : html`<span class="ws-strip-btn" aria-hidden="true">${inner}</span>`}
          </li>`
        })}
      </ol>
      <p class="ws-strip-current" lang="de">
        <span class="ws-strip-pos">${pos + 1}/${total}</span><span class="ws-strip-label" lang="de">${curLabel}</span>
      </p>
    </nav>`
  }

  protected render(vm: WorkshopRunnerVm): TemplateResult {
    const w = vm.workshop.value
    const err = vm.error.value
    const rootUrn = (this.urn ?? "") as Urn
    if (vm.notFound.value) {
      return html`
        <section class="surface workshop-runner" urn=${rootUrn} aria-labelledby="runner-title">
          <h1 id="runner-title" class="surface-title notfound-title" lang="de">${vm.t("workshop.runner.notFound.title")}</h1>
          <p class="surface-empty" lang="de">${vm.t("workshop.runner.notFound.body")}</p>
          <a class="chip" href="/workshops" data-act="nav" data-url="/workshops" lang="de">${vm.t("workshop.runner.notFound.back")}</a>
        </section>`
    }
    if (!w) {
      return html`
        <section class="surface workshop-runner" urn=${rootUrn} aria-labelledby="runner-title">
          <h1 id="runner-title" class="surface-title" lang="de">${vm.t("surface.workshopRunner.title")}</h1>
          ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
          ${vm.loaded.value
            ? html`<p class="surface-empty" lang="de">${vm.t("workshop.runner.notStarted")}</p>
                <button data-act="generate-stimulus" lang="de">${vm.t("workshop.action.start")}</button>`
            : nothing}
        </section>`
    }
    const completed = w.status === "completed"
    return html`
      <section class="surface workshop-runner" urn=${rootUrn} aria-labelledby="runner-title">
        <div class=${classMap({ "ws-frame": true, "is-completed": completed })}>
          ${this.pathRail(vm, w, completed)}
          ${this.pathStrip(vm, w, completed)}
          <section class="ws-log" aria-labelledby="runner-title">
            <p class="ws-kicker eyebrow" lang="de">${vm.t("workshop.runner.kicker")}</p>
            <h1 id="runner-title" class="surface-title ws-title" lang="de">${vm.t("surface.workshopRunner.title")}</h1>
            <p class="surface-lead ws-lede" lang="de">${vm.t("workshop.runner.lead")}</p>
            ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

            <ol class="timeline-list thread" role="list">
              ${repeat(w.steps, (s) => s.stepId, (s, i) => this.timelineNode(vm, s, i, w.cursor, completed, w.steps.length))}
            </ol>

            ${!completed && vm.requiredAnswered()
              ? html`<section class="workshop-consolidate surface-panel">
                  <p lang="de">${vm.t("workshop.consolidate.ready")}</p>
                  <button data-act="consolidate-workshop" class=${classMap({ "is-pending": vm.busy.value === "consolidate", "is-x-prominent": true })} ?disabled=${vm.busy.value === "consolidate"} lang="de">
                    ${vm.busy.value === "consolidate" ? vm.t("workshop.consolidate.busy") : vm.t("workshop.consolidate.action")}
                  </button>
                </section>`
              : nothing}

            ${completed ? this.consolidationView(vm, w) : nothing}

            ${this.findingsFallback(vm, w)}
          </section>
          ${this.findingsRail(vm, w)}
        </div>
      </section>`
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  private readNarrative(event: Event): string {
    const root = (event.target as HTMLElement).closest(".node-active") ?? (event.target as HTMLElement).closest(".surface")
    return root?.querySelector<HTMLTextAreaElement>("textarea.card-narrative")?.value ?? ""
  }
  private readDifferential(event: Event): number {
    const root = (event.target as HTMLElement).closest(".node-active")
    const raw = root?.querySelector<HTMLInputElement>("input.differential-input")?.value
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0.5
  }

  protected override actions(vm: WorkshopRunnerVm): ActionMap {
    return {
      "generate-stimulus": () => {
        if (!vm.workshop.value) void vm.start()
        else void vm.generate()
      },
      "pick-option": ({ role, id }) => {
        if (!id) return
        // Single-select per pole; clicking the held cell deselects; a row can NEVER hold
        // both poles (best-worst §3) — claiming one pole releases the other on that row.
        if (role === "best") {
          vm.bestId.set(vm.bestId.value === id ? null : id)
          if (vm.worstId.value === id) vm.worstId.set(null)
        } else if (role === "worst") {
          vm.worstId.set(vm.worstId.value === id ? null : id)
          if (vm.bestId.value === id) vm.bestId.set(null)
        } else if (role === "choice") {
          vm.choiceId.set(vm.choiceId.value === id ? null : id)
        }
      },
      "toggle-veto": ({ id }) => {
        if (id) vm.toggleVeto(id)
      },
      "mark-nuance": ({ nuance }) => {
        void vm.submit({ kind: "nuance", nuance: nuance ?? "depends" })
      },
      "submit-turn": ({ kind, choice }, event) => {
        switch (kind) {
          case "pairwise":
            void vm.submit({ kind: "pairwise", choice: choice ?? "A" })
            break
          case "best_worst":
            if (vm.bestId.value && vm.worstId.value) void vm.submit({ kind: "best_worst", best: vm.bestId.value, worst: vm.worstId.value })
            break
          case "vignette":
          case "simulation":
            if (vm.choiceId.value)
              void vm.submit({ kind, choice: vm.choiceId.value, vetoes: vm.vetoes.value.map((v) => ({ id: v, intensity: "niemals" })) })
            break
          case "semantic_differential":
            void vm.submit({ kind: "semantic_differential", position: this.readDifferential(event) })
            break
          case "open_narrative": {
            const text = this.readNarrative(event).trim()
            if (text.length > 0) void vm.submit({ kind: "open_narrative", text })
            break
          }
        }
      },
      "goto-step": ({ stepId }, event) => {
        if (!stepId) return
        // Jump the center log to the addressed turn node (urn-bound), no route change needed.
        const root = (event.target as HTMLElement).closest(".workshop-runner")
        root?.querySelector(`.timeline-node[urn="urn:career:workshop-step:${stepId}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" })
      },
      "consolidate-workshop": () => void vm.consolidate(),
      "update-targeting": (_payload, event) => {
        const form = (event.target as HTMLElement).closest("form")
        if (!form) return
        const read = (name: string) =>
          (form.querySelector<HTMLTextAreaElement>(`textarea[name="${name}"]`)?.value ?? "")
            .split("\n")
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        const current = vm.workshop.value?.targeting
        vm.saveTargeting({
          roles: read("roles"),
          queries: read("queries"),
          seniority: read("seniority"),
          locations: read("locations"),
          excludeKeywords: read("excludeKeywords"),
          summary: current?.summary ?? "",
        })
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
